/**
 * Energy Management System
 * Handles energy calculations, consumption, and restoration
 * Based on game_model.yaml energy formulas and transitions
 */

import { GameState, StageId } from '../models/GameState';
import { PlayerState, PlayerRole } from '../models/Player';

export interface EnergyTransition {
  expend: number;
  consumePacks: number;
  restore: number;
  net: number;
}

export class EnergyManager {
  /**
   * Energy pack value (10% energy per pack)
   */
  private readonly ENERGY_PACK_VALUE = 10;

  /**
   * Calculate starting energy from player name length
   * Formula from game_model.yaml
   */
  calculateStartingEnergy(name: string): number {
    const length = name.length;

    if (length <= 5) return 65;
    if (length <= 10) return 75;
    if (length <= 15) return 85;
    return 100;
  }

  /**
   * Get energy transition rules for a stage
   */
  getStageEnergyTransition(
    stage: StageId,
    role: PlayerRole,
    q9Answer?: string
  ): EnergyTransition {
    switch (stage) {
      case 1:
        // Stage 1: Storm (Fixer takes more damage if injured)
        if (role === 'FIXER') {
          return { expend: 35, consumePacks: 1, restore: 10, net: -25 };
        }
        return { expend: 30, consumePacks: 1, restore: 10, net: -20 };

      case 2:
        // Stage 2: Cave healing (+30 bonus)
        return { expend: 0, consumePacks: 1, restore: 30, net: 30 };

      case 3:
        // Stage 3: Descent
        return { expend: 20, consumePacks: 1, restore: 10, net: -10 };

      case 4:
        // Stage 4: Activity-based consumption
        switch (q9Answer) {
          case 'A': // Stay on ship
            return { expend: 20, consumePacks: 1, restore: 10, net: -10 };
          case 'B': // Capture Ray
            return { expend: 30, consumePacks: 2, restore: 20, net: -10 };
          case 'C': // Cleanup
            return { expend: 35, consumePacks: 1, restore: 10, net: -25 };
          case 'D': // Capture Octopus
            return { expend: 40, consumePacks: 1, restore: 10, net: -30 };
          default:
            return { expend: 20, consumePacks: 1, restore: 10, net: -10 };
        }

      default:
        return { expend: 0, consumePacks: 0, restore: 0, net: 0 };
    }
  }

  /**
   * Apply stage energy consumption to a player
   */
  applyStageEnergyConsumption(
    player: PlayerState,
    stage: StageId,
    _gameState: GameState
  ): { player: PlayerState; packsUsed: number } {
    let q9Answer: string | undefined;

    if (stage === 4) {
      // Get player's Q9 answer for activity-based consumption
      q9Answer = player.decisions.get('Q9') as string;
    }

    const transition = this.getStageEnergyTransition(stage, player.role, q9Answer);

    // Calculate new energy
    let newEnergy = player.energy;
    newEnergy -= transition.expend; // Expend energy
    newEnergy = Math.max(0, newEnergy); // Can't go below 0

    // Restore from energy packs
    const energyRestored = transition.restore;
    newEnergy = Math.min(100, newEnergy + energyRestored);

    // Check if player died
    const isAlive = newEnergy > 0;

    return {
      player: {
        ...player,
        energy: newEnergy,
        isAlive
      },
      packsUsed: transition.consumePacks
    };
  }

  /**
   * Consume energy packs for a player (Q12 answer)
   */
  consumeEnergyPacks(player: PlayerState, packsCount: number): PlayerState {
    const energyRestored = packsCount * this.ENERGY_PACK_VALUE;
    const newEnergy = Math.min(100, player.energy + energyRestored);

    return {
      ...player,
      energy: newEnergy
    };
  }

  /**
   * Check if player can survive Q14 swimming decision
   */
  canSurviveSwimming(player: PlayerState, q14Answer: string): boolean {
    // Q14 answer C (Swim for surface) is dangerous
    if (q14Answer !== 'C') {
      return true; // Safe choices
    }

    // Only DIVER and HUNTER can survive swimming (energy check still applies)
    if (player.role === 'DIVER' || player.role === 'HUNTER') {
      return player.energy > 0;
    }

    // Other roles die if they try to swim with low energy
    return player.energy > 30; // Need sufficient energy to survive
  }

  /**
   * Apply Q14 death conditions
   */
  applyQ14DeathConditions(player: PlayerState, q14Answer: string): PlayerState {
    if (!this.canSurviveSwimming(player, q14Answer)) {
      return {
        ...player,
        isAlive: false,
        energy: 0
      };
    }

    return player;
  }

  /**
   * Get energy status indicator
   */
  getEnergyStatus(energy: number): {
    level: 'critical' | 'low' | 'medium' | 'good' | 'excellent';
    color: string;
    message: string;
  } {
    if (energy === 0) {
      return {
        level: 'critical',
        color: 'red',
        message: 'CRITICAL - No energy remaining'
      };
    }
    if (energy <= 20) {
      return {
        level: 'critical',
        color: 'red',
        message: 'CRITICAL - Energy dangerously low'
      };
    }
    if (energy <= 40) {
      return {
        level: 'low',
        color: 'orange',
        message: 'LOW - Conserve energy'
      };
    }
    if (energy <= 60) {
      return {
        level: 'medium',
        color: 'amber',
        message: 'MEDIUM - Monitor energy levels'
      };
    }
    if (energy <= 80) {
      return {
        level: 'good',
        color: 'green',
        message: 'GOOD - Energy stable'
      };
    }
    return {
      level: 'excellent',
      color: 'blue',
      message: 'EXCELLENT - Energy optimal'
    };
  }

  /**
   * Calculate team average energy
   */
  calculateTeamAverageEnergy(players: PlayerState[]): number {
    if (players.length === 0) return 0;

    const total = players.reduce((sum, p) => sum + p.energy, 0);
    return Math.round(total / players.length);
  }

  /**
   * Get players with critical energy
   */
  getCriticalEnergyPlayers(players: PlayerState[]): PlayerState[] {
    return players.filter(p => p.energy <= 20 && p.isAlive);
  }

  /**
   * Check if any player needs immediate attention
   */
  hasEnergyEmergency(players: PlayerState[]): boolean {
    return players.some(p => p.energy <= 20 && p.isAlive);
  }

  /**
   * Estimate energy remaining for stages
   */
  estimateEnergyForRemainingStages(
    player: PlayerState,
    currentStage: StageId
  ): { canComplete: boolean; projectedEnergy: number } {
    let projectedEnergy = player.energy;
    const stagesRemaining = 5 - currentStage;

    // Rough estimate: -15 energy per remaining stage
    projectedEnergy -= stagesRemaining * 15;

    return {
      canComplete: projectedEnergy > 0,
      projectedEnergy: Math.max(0, projectedEnergy)
    };
  }

  /**
   * Apply poisoning effect (Diver Q7=B)
   */
  applyPoisoning(player: PlayerState): PlayerState {
    if (!player.isPoisoned) return player;

    // Poisoning reduces energy recovery effectiveness
    // (This could be applied during stage transitions)
    return player;
  }

  /**
   * Get energy pack consumption recommendation
   */
  getEnergyPackRecommendation(
    player: PlayerState,
    teamEnergyPacks: number,
    playersCount: number
  ): {
    recommended: number;
    reasoning: string;
  } {
    const currentEnergy = player.energy;
    const fairShare = Math.floor(teamEnergyPacks / playersCount);

    if (currentEnergy <= 20) {
      return {
        recommended: Math.min(fairShare + 2, teamEnergyPacks),
        reasoning: 'Critical energy - take extra packs to survive'
      };
    }

    if (currentEnergy <= 40) {
      return {
        recommended: Math.min(fairShare + 1, teamEnergyPacks),
        reasoning: 'Low energy - take slightly more than fair share'
      };
    }

    if (currentEnergy >= 80) {
      return {
        recommended: Math.max(fairShare - 1, 0),
        reasoning: 'High energy - consider taking less to help team'
      };
    }

    return {
      recommended: fairShare,
      reasoning: 'Take your fair share of energy packs'
    };
  }
}

// Singleton instance
export const energyManager = new EnergyManager();
